import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableHead,
  TableRow, Chip, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Stack, Alert, Tooltip,
  CircularProgress,
} from "@mui/material";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../api";

const ROLE_LABEL = (t) => ({
  owner: t("team.roleOwner"),
  admin: t("team.roleAdmin"),
  employee: t("team.roleEmployee"),
});
const STATUS_CHIP = (t) => ({
  active: { label: t("team.statusActive"), color: "success" },
  invited: { label: t("team.statusInvited"), color: "warning" },
  disabled: { label: t("team.statusDisabled"), color: "default" },
});

export default function Team() {
  const { t } = useTranslation();
  const { activeWorkspaceId, workspaceRole } = useAuth();
  const isOwner = workspaceRole === "owner";
  const canManageStaff = workspaceRole === "owner" || workspaceRole === "admin";

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [inviteOpen, setInviteOpen] = useState(false);
  const [form, setForm] = useState({ phone: "", name: "", role: "employee" });
  const [inviteErr, setInviteErr] = useState("");
  const [saving, setSaving] = useState(false);

  const roleLabel = ROLE_LABEL(t);
  const statusChip = STATUS_CHIP(t);

  const load = useCallback(async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    setError("");
    try {
      const res = await api.getWorkspaceMembers(activeWorkspaceId);
      setMembers(res.members || []);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }, [activeWorkspaceId]);

  useEffect(() => {
    load();
  }, [load]);

  const openInvite = () => {
    setForm({ phone: "", name: "", role: "employee" });
    setInviteErr("");
    setInviteOpen(true);
  };

  const submitInvite = async () => {
    setSaving(true);
    setInviteErr("");
    try {
      await api.inviteMember(activeWorkspaceId, {
        phone: form.phone,
        name: form.name || undefined,
        role: form.role,
      });
      setInviteOpen(false);
      await load();
    } catch (e) {
      setInviteErr(e.message);
    }
    setSaving(false);
  };

  const changeRole = async (m, role) => {
    try {
      await api.updateMember(activeWorkspaceId, m.userId, { role });
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  const toggleStatus = async (m) => {
    const status = m.status === "disabled" ? "active" : "disabled";
    try {
      await api.updateMember(activeWorkspaceId, m.userId, { status });
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  const remove = async (m) => {
    if (!window.confirm(t("team.confirmRemove"))) return;
    try {
      await api.removeMember(activeWorkspaceId, m.userId);
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  // Чи можна керувати конкретним учасником поточному користувачу.
  const canEdit = (m) => {
    if (m.role === "owner") return false;
    if (isOwner) return true;
    // admin — лише співробітники
    return m.role === "employee";
  };

  if (!activeWorkspaceId) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography color="text.secondary">{t("team.noWorkspace")}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 2 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Typography variant="h5">{t("team.title")}</Typography>
        {canManageStaff && (
          <Button
            variant="contained"
            startIcon={<PersonAddAlt1Icon />}
            onClick={openInvite}
            sx={{ textTransform: "none" }}
          >
            {t("team.invite")}
          </Button>
        )}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : members.length === 0 ? (
        <Typography color="text.secondary">{t("team.empty")}</Typography>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("team.name")}</TableCell>
                <TableCell>{t("team.phone")}</TableCell>
                <TableCell>{t("team.role")}</TableCell>
                <TableCell>{t("team.status")}</TableCell>
                <TableCell align="right">{t("team.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {members.map((m) => (
                <TableRow key={m.userId}>
                  <TableCell>{m.name}</TableCell>
                  <TableCell>{m.phone}</TableCell>
                  <TableCell>
                    {/* Роль змінює лише власник, і лише не-власникам */}
                    {isOwner && m.role !== "owner" ? (
                      <TextField
                        select
                        size="small"
                        value={m.role}
                        onChange={(e) => changeRole(m, e.target.value)}
                        variant="standard"
                        sx={{ minWidth: 120 }}
                      >
                        <MenuItem value="admin">{roleLabel.admin}</MenuItem>
                        <MenuItem value="employee">
                          {roleLabel.employee}
                        </MenuItem>
                      </TextField>
                    ) : (
                      roleLabel[m.role] || m.role
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={statusChip[m.status]?.label || m.status}
                      color={statusChip[m.status]?.color || "default"}
                      variant={m.status === "disabled" ? "outlined" : "filled"}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {canEdit(m) && (
                      <>
                        <Tooltip
                          title={
                            m.status === "disabled"
                              ? t("team.enable")
                              : t("team.disable")
                          }
                        >
                          <IconButton
                            size="small"
                            onClick={() => toggleStatus(m)}
                          >
                            {m.status === "disabled" ? (
                              <CheckCircleOutlineIcon fontSize="small" />
                            ) : (
                              <BlockIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t("team.remove")}>
                          <IconButton size="small" onClick={() => remove(m)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* Діалог запрошення */}
      <Dialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{t("team.inviteTitle")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {inviteErr && <Alert severity="error">{inviteErr}</Alert>}
            <TextField
              label={t("team.phone")}
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              fullWidth
              autoFocus
              placeholder="+48…"
            />
            <TextField
              label={t("team.inviteNameOpt")}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              fullWidth
            />
            <TextField
              select
              label={t("team.role")}
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              fullWidth
            >
              <MenuItem value="employee">{roleLabel.employee}</MenuItem>
              {/* Призначати адмінів може лише власник */}
              {isOwner && <MenuItem value="admin">{roleLabel.admin}</MenuItem>}
            </TextField>
            <Typography variant="caption" color="text.secondary">
              {t("team.inviteHint")}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteOpen(false)}>
            {t("team.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={submitInvite}
            disabled={saving || !form.phone}
          >
            {t("team.send")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
